import {useMemo, type ComponentType} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
  BookOpen,
  CalendarClock,
  ClipboardList,
  Database,
  FileSpreadsheet,
  FolderArchive,
  GraduationCap,
  Grid3X3,
  Inbox,
  LibraryBig,
  MessageSquare,
  Radio,
  Truck,
  User,
  Users,
} from 'lucide-react';
import {FaDog} from 'react-icons/fa';
import {PiVaultLight} from 'react-icons/pi';
import {type UserRole, userRoles} from '@/matic/types/roles';
import {useAppContext} from '@/matic/providers/AppProvider';
import {tenantFeatures} from '@/matic/config/features';
import {getRecordTypesApi} from '@/matic/features/records/api/record-types-api';
import {useUiMode, type UiMode} from '@/matic/features/_shared/hooks/useUiMode';
import {useTenantSettings} from '@/matic/features/_shared/hooks/useTenantSettings';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';

export type PinnedRecordType = {
  id: string;
  name: string;
};

export type NavItemConfig = {
  label: string;
  path?: string;
  icon?: ComponentType<{className?: string}>;
  children?: NavItemConfig[];
  isOpen?: boolean;
  badge?: string;
};

export type NavSectionConfig = {
  title?: string;
  items: NavItemConfig[];
};

/**
 * Base QMS items - available to all users
 * VAULT path is set dynamically based on role in getNavConfig()
 */
const baseQmsItems: NavItemConfig[] = [
  {
    label: 'VAULT',
    icon: PiVaultLight,
    path: '/documents',
  },
  {
    label: 'TRAINING',
    icon: GraduationCap,
    children: [
      {
        label: 'My Training',
        icon: BookOpen,
        path: '/my-training',
      },
    ],
  },
];

/**
 * User section - available to all users
 */
const userSection: NavSectionConfig = {
  title: 'USER',
  items: [
    {label: 'NOTIFICATIONS', path: '/notifications', icon: Inbox},
    {label: 'MY PROFILE', path: '/profile', icon: User},
  ],
};

/**
 * Administration section - only for admin users
 *
 * Users + Groups are tabs on a single page (``/admin/users``); the
 * sidebar surfaces only the parent entry. ``/admin/groups`` remains
 * a redirect for legacy deep-links.
 */
const administrationSection: NavSectionConfig = {
  title: 'ADMINISTRATION',
  items: [{label: 'USERS & GROUPS', path: '/admin/users', icon: Users}],
};

/**
 * Get navigation config based on user role
 * @param userRole - The role of the current user
 * @returns Navigation sections available for the user
 */
const triageNavItem: NavItemConfig = {
  label: 'INBOX',
  icon: Radio,
  path: '/inbox',
};

/**
 * Build the RECORDS entry, with pinned Record Types as children for the roles
 * that can manage them. Only called when the tenant has QMS Records enabled.
 */
function buildRecordsNavItem(
  userRole: UserRole | undefined,
  pinnedRecordTypes?: PinnedRecordType[]
): NavItemConfig {
  const recordsItem: NavItemConfig = {
    label: 'RECORDS',
    icon: FolderArchive,
    path: '/records',
  };

  const isRecordsAdmin =
    userRole === userRoles.admin || userRole === userRoles.quality;

  if (isRecordsAdmin && pinnedRecordTypes && pinnedRecordTypes.length > 0) {
    recordsItem.children = pinnedRecordTypes.map((rt) => ({
      label: rt.name,
      icon: ClipboardList,
      path: `/records/types/${rt.id}`,
    }));
  }

  return recordsItem;
}

/**
 * QUOTES follows the tenant's quoting engine at runtime (`/quoting/capabilities`), not a
 * build flag. An AES tenant's shell draws its own, so it's left out there.
 */
function quotesNavItems(
  quotingEnabled: boolean,
  quotesFirst: boolean
): NavItemConfig[] {
  return quotingEnabled && !quotesFirst
    ? [{label: 'QUOTES', icon: FileSpreadsheet, path: '/quotes'}]
    : [];
}

export function getNavConfig(
  userRole: UserRole | undefined,
  tenantId?: string,
  pinnedRecordTypes?: PinnedRecordType[],
  uiMode: UiMode = 'classic',
  qmsRecordsEnabled = false,
  quotingEnabled = false,
  quotesFirst = false
): NavSectionConfig[] {
  // Otto (Relay) mode renders the consolidated Library rail directly in
  // `SidebarNav` (browse + conversations), not a nav-section list — so the
  // config contributes no sections here.
  if (uiMode === 'otto') return [];

  // Build QMS items based on role - deep clone to avoid mutation
  const qmsItems = baseQmsItems.map((item) => {
    // VAULT: lands on the Documents tab by default for everyone
    if (item.label === 'VAULT') {
      return {
        ...item,
        path: '/documents',
      };
    }

    if (item.label === 'TRAINING') {
      const isAdminOrQuality =
        userRole === userRoles.admin || userRole === userRoles.quality;

      // The instructor-approval inbox now lives inside ``My Training``
      // as a tab (``Approvals``), not as a standalone sidebar entry.
      // The pending count surfaces as a badge on My Training (and on
      // the parent TRAINING icon when the sidebar is collapsed).
      if (isAdminOrQuality) {
        // Competency Matrix now lives as a tab inside the Manager
        // page (``/training/manager?tab=matrix``) instead of a
        // standalone sidebar entry.
        return {
          ...item,
          path: '/my-training', // Default click action
          children: [
            {
              label: 'My Training',
              icon: BookOpen,
              path: '/my-training',
            },
            {
              label: 'Manager',
              icon: LibraryBig,
              path: '/training/manager',
            },
          ],
        };
      }

      // Non-admin/quality: TRAINING collapses to a single direct link
      // — no dropdown needed when there's only one child.
      return {
        label: 'TRAINING',
        icon: GraduationCap,
        path: '/my-training',
      };
    }

    return item;
  });

  // RECORDS is the legacy QMS Records domain, gated per tenant on
  // `qms_records_enabled` (default off). Nav-only: routes stay registered and
  // the API is untouched, so a deep link still resolves for tenants that had
  // it hidden. Defaults off while tenant settings load, so the common
  // (flag-off) case never flashes the entry.
  if (qmsRecordsEnabled) {
    // Insert RECORDS after VAULT, before TRAINING
    qmsItems.splice(1, 0, buildRecordsNavItem(userRole, pinnedRecordTypes));
  }

  // Conditionally add Scout (with optional Lattice child) at the beginning
  if (!tenantId || tenantFeatures.scout(tenantId)) {
    const scoutItem: NavItemConfig = {
      label: 'SCOUT',
      icon: FaDog,
      path: '/scout',
    };

    if (tenantId && tenantFeatures.lattice(tenantId)) {
      scoutItem.children = [
        {
          label: 'Chat',
          icon: MessageSquare,
          path: '/scout',
        },
        {
          label: 'Lattice',
          icon: Grid3X3,
          path: '/lattice',
        },
      ];
    }

    qmsItems.unshift(scoutItem);
  }

  if (tenantId && tenantFeatures.scheduling(tenantId)) {
    qmsItems.push({
      label: 'SCHEDULE',
      icon: CalendarClock,
      path: '/scheduling/calendar',
    });
  }

  if (tenantId && tenantFeatures.supplierQuality(tenantId)) {
    qmsItems.push({
      label: 'SUPPLIERS',
      icon: Truck,
      path: '/suppliers',
    });
  }

  if (tenantId && tenantFeatures.relay(tenantId)) {
    qmsItems.push(triageNavItem);
  }

  qmsItems.push(...quotesNavItems(quotingEnabled, quotesFirst));

  // Master Data is visible to all users when the MDM feature is enabled.
  if (!tenantId || tenantFeatures.mdm(tenantId)) {
    qmsItems.push({
      label: 'MASTER DATA',
      icon: Database,
      path: '/admin/master-data',
    });
  }

  const sections: NavSectionConfig[] = [
    {
      title: 'QMS',
      items: qmsItems,
    },
    userSection,
  ];

  // Show administration section for admin and quality users.
  // Quality users see the same combined page, but the Users tab is
  // hidden in-page — relabel the sidebar entry accordingly so the
  // affordance matches what they'll actually see.
  if (userRole === userRoles.admin || userRole === userRoles.quality) {
    const adminItems = administrationSection.items.map((item) => {
      if (item.path === '/admin/users' && userRole === userRoles.quality) {
        return {...item, label: 'GROUPS'};
      }

      return item;
    });

    sections.push({...administrationSection, items: adminItems});
  }

  return sections;
}

/**
 * Hook to get role-based navigation config
 * Memoizes the config to only recompute when user role changes
 * @returns Navigation sections based on current user's role
 */
export function useNavConfig(): NavSectionConfig[] {
  const {userRole, tenantId} = useAppContext();
  const {mode: uiMode} = useUiMode();
  // Runtime DB state, not a compile-time allowlist: flipping the tenant flag
  // must change the sidebar without a frontend deploy.
  const {data: tenantSettings} = useTenantSettings();
  const qmsRecordsEnabled = tenantSettings?.qmsRecordsEnabled ?? false;
  // Off while capabilities load, so tenants without quoting never flash the entry.
  const {enabled: quotingEnabled} = useQuotingAvailability();
  const quotesFirst = useQuotesFirst() === true;
  // UseOttoSidebarObjects seeds the localStorage store and fetches required
  // types + records. Only active in otto mode (queries are cheap when disabled).
  const isRecordsAdmin =
    userRole === userRoles.admin || userRole === userRoles.quality;

  const {data: pinnedRecordTypes} = useQuery({
    queryKey: ['record-types', 'pinned'],
    async queryFn() {
      const api = getRecordTypesApi();
      const result = await api.queryRecordTypes({
        filters: [{field: 'is_pinned', eq: true}],
        sort: [{field: 'name', direction: 'asc'}],
        pagination: {limit: 20, offset: 0},
      });
      return result.items.map((rt) => ({id: rt.id, name: rt.name}));
    },
    enabled: isRecordsAdmin && uiMode !== 'otto' && qmsRecordsEnabled,
    staleTime: 30_000,
  });

  return useMemo(
    () =>
      getNavConfig(
        userRole,
        tenantId,
        pinnedRecordTypes,
        uiMode,
        qmsRecordsEnabled,
        quotingEnabled,
        quotesFirst
      ),
    [
      userRole,
      tenantId,
      pinnedRecordTypes,
      uiMode,
      qmsRecordsEnabled,
      quotingEnabled,
      quotesFirst,
    ]
  );
}
